import { css } from '@emotion/core';
import { Button, IconButton, Paper, Tooltip } from '@material-ui/core';
import { Close, DragIndicator, GetApp } from '@material-ui/icons';
import React, { createContext, useCallback, useContext, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { downloadFile } from '../actions';
import { zIndexes } from '../common/css-variables';
import { config } from '../config';
import { useDraggable } from './use-draggable';
import { useReduxDispatch, useReduxState } from './use-redux';

export interface AudioRecording {
    path: string;
    filename: string;
    title?: React.ReactNode;
}

interface AudioPlayerContextValue {
    play: (recording: AudioRecording) => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextValue>(undefined);
const playerElevation = 8;

const styles = css`
    position: fixed;
    width: 520px;
    max-width: calc(100vw - 24px);
    max-height: calc(100vh - 24px);
    overflow: auto;
    padding: 12px;
    box-sizing: border-box;
    z-index: ${zIndexes.audioPlayer};

    .audio-player-header {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        margin-bottom: 8px;
    }
    .audio-player-label {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        flex: 1;
        min-width: 0;
        padding: 3px 0;
        cursor: grab;
        touch-action: none;
        user-select: none;
        &:active {
            cursor: grabbing;
        }
    }
    .audio-player-title {
        min-width: 0;
        line-height: 1.5;
        overflow-wrap: anywhere;
    }
    .audio-player-controls {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    audio {
        display: block;
        flex: 1;
        min-width: 0;
    }
    .audio-player-error {
        margin-top: 8px;
        font-size: 12px;
    }
`;

export const AudioPlayerProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const titleId = useId();
    const [recording, setRecording] = useState<AudioRecording>(null);
    const [error, setError] = useState(false);
    const open = !!recording;
    const dispatch = useReduxDispatch();
    const downloading = useReduxState((state) => state.pendingRequests.has(`download-file-${recording?.path}`));
    const { ref, position, handlePointerDown, handlePointerMove, handleLostPointerCapture } = useDraggable(
        open,
        'audio-player-position'
    );

    const play = useCallback((file: AudioRecording) => {
        const audio = audioRef.current;
        const url = `${config.FilesHost}/${file.path.split('/').map(encodeURIComponent).join('/')}`;
        setRecording(file);
        setError(false);
        if (audio.getAttribute('src') !== url || audio.error) audio.src = url;
        audio.play().catch((playError: Error) => {
            if (playError.name !== 'AbortError') setError(true);
        });
    }, []);

    const handleClose = () => {
        audioRef.current.pause();
        audioRef.current.removeAttribute('src');
        audioRef.current.load();
        setRecording(null);
        setError(false);
    };
    const handleError = () => setError(true);
    const handlePlay = () => setError(false);
    const handleRetry = () => play(recording);
    const handleDownload = () => dispatch(downloadFile(recording.path, recording.filename));
    const handleKeyDown = (event: React.KeyboardEvent) => event.stopPropagation();

    const value = useMemo(() => ({ play }), [play]);
    const title = recording?.title ?? recording?.filename;
    const positionStyle: React.CSSProperties = {
        display: recording ? 'block' : 'none',
        left: position?.x,
        top: position?.y
    };
    const message = error ? (
        <div className="audio-player-error" role="status">
            Unable to play this recording.
            <Button size="small" onClick={handleRetry}>
                Retry
            </Button>
        </div>
    ) : null;
    const downloadButton = open ? (
        <Tooltip title="Download recording">
            <span>
                <IconButton
                    size="small"
                    aria-label="Download recording"
                    onClick={handleDownload}
                    disabled={downloading}
                >
                    <GetApp />
                </IconButton>
            </span>
        </Tooltip>
    ) : null;
    const player = (
        <Paper
            ref={ref}
            css={styles}
            elevation={playerElevation}
            role="region"
            aria-label="Audio player"
            onKeyDown={handleKeyDown}
            style={positionStyle}
        >
            <div className="audio-player-header">
                <div
                    className="audio-player-label"
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onLostPointerCapture={handleLostPointerCapture}
                >
                    <DragIndicator color="action" titleAccess="Drag to move" />
                    <div id={titleId} className="audio-player-title">
                        {title}
                    </div>
                </div>
                <IconButton size="small" aria-label="Close audio player" onClick={handleClose}>
                    <Close />
                </IconButton>
            </div>
            <div className="audio-player-controls">
                <audio
                    ref={audioRef}
                    controls={true}
                    preload="none"
                    aria-labelledby={titleId}
                    onError={handleError}
                    onPlay={handlePlay}
                />
                {downloadButton}
            </div>
            {message}
        </Paper>
    );

    return (
        <AudioPlayerContext.Provider value={value}>
            {children}
            {createPortal(player, document.body)}
        </AudioPlayerContext.Provider>
    );
};

export const useAudioPlayer = () => useContext(AudioPlayerContext);
